/**
 * SuperAdminReports — Internal reports across dealers.
 *
 * D5 spec: margin avg, seat utilization, account health.
 * Route: /super-admin/reports
 */

import React, { useState, useEffect } from 'react';
import PageHeader from '../ui/PageHeader';
import { SkeletonCard } from '../ui/Skeleton';
import { superAdminAPI } from '../../services/api';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';

export default function SuperAdminReports() {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Try getReports first, fall back to getDealers for basic metrics
        let data;
        try {
          const res = await superAdminAPI.getReports();
          data = Array.isArray(res) ? res : (res?.data || res?.items || []);
        } catch {
          // getReports may not be implemented yet — fall back to dealer list
          const res = await superAdminAPI.getDealers();
          data = Array.isArray(res) ? res : (res?.data || res?.items || []);
        }
        if (mounted) setDealers(data);
      } catch {
        // IDI-65: both the reports and the dealer-list fallback failed. Show an
        // error state rather than computing KPIs from an empty array (which
        // renders as legitimate-looking zeros).
        if (mounted) { setLoadError(true); setDealers([]); }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const kpis = computeKpis(dealers);

  if (loadError) {
    return (
      <div className="space-y-6">
        <PageHeader title="Super-Admin Reports" subtitle="Internal reports across all contractors" />
        <div
          className="flex flex-col items-center justify-center text-center py-16 px-6 rounded-lg border"
          style={{ borderColor: 'var(--color-border)', background: 'var(--color-surface)' }}
        >
          <ExclamationTriangleIcon className="h-10 w-10 mb-3" style={{ color: 'var(--color-text-secondary)' }} aria-hidden="true" />
          <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Couldn't load reports</p>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>The request failed — these are not real zeros. Try again.</p>
          <div className="mt-4">
            <Button variant="secondary" onClick={() => window.location.reload()}>Try again</Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Super-Admin Reports" subtitle="Internal reports across all contractors" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Super-Admin Reports"
        subtitle="Internal reports across all contractors"
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ReportCard title="Total Contractors" value={kpis.totalDealers} subtitle="Registered accounts" />
        <ReportCard title="Active Users" value={kpis.activeUsers} subtitle="Across all contractors" />
        <ReportCard title="Seat Utilization" value={kpis.seatUtilization} subtitle="Paid seats used / total" />
        <ReportCard title="Account Health" value={kpis.accountHealth} subtitle="Active / Inactive contractors" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ReportCard title="Avg Margin" value={kpis.avgMargin} subtitle="Across all contractors" />
        <ReportCard title="Engagement" value={kpis.engagement} subtitle="Contractors active this month" />
        <ReportCard title="Avg Seats/Contractor" value={kpis.avgSeats} subtitle="Mean paid seats" />
      </div>
    </div>
  );
}

function computeKpis(dealers) {
  if (!dealers || dealers.length === 0) {
    return { totalDealers: '0', activeUsers: '0', avgMargin: '—', seatUtilization: '—', accountHealth: '0 / 0', engagement: '—', avgSeats: '—' };
  }

  const totalDealers = String(dealers.length);

  // Active users (sum of seatsUsed across all dealers)
  const used = dealers.reduce((a, d) => a + (d.seatsUsed ?? d.seats_used ?? 0), 0);
  const total = dealers.reduce((a, d) => a + (d.seatsTotal ?? d.seats_total ?? d.paid_seats ?? 0), 0);
  const activeUsers = String(used || dealers.length);

  // Avg margin (dealers may carry avgMargin as a fraction or percent).
  const margins = dealers
    .map((d) => d.avgMargin ?? d.margin)
    .filter((m) => typeof m === 'number' && !Number.isNaN(m));
  const avgMargin = margins.length
    ? `${Math.round((margins.reduce((a, b) => a + b, 0) / margins.length) * (margins[0] <= 1 ? 100 : 1))}%`
    : '—';

  // Seat utilization.
  const seatUtilization = total > 0 ? `${Math.round((used / total) * 100)}%` : '—';

  // Account health.
  const active = dealers.filter((d) => (d.status || (d.active === false ? 'inactive' : 'active')) === 'active').length;
  const inactive = dealers.length - active;
  const accountHealth = `${active} / ${inactive}`;

  // Engagement (dealers with lastActive in the last 30 days)
  const now = Date.now();
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  const recentlyActive = dealers.filter(d => {
    const last = d.lastActive || d.last_active || d.updatedAt || d.updated_at;
    if (!last) return false;
    return (now - new Date(last).getTime()) < thirtyDays;
  }).length;
  const engagement = recentlyActive > 0 ? `${recentlyActive} / ${dealers.length}` : '—';

  // Avg seats per dealer
  const avgSeats = total > 0 ? (total / dealers.length).toFixed(1) : '—';

  return { totalDealers, activeUsers, avgMargin, seatUtilization, accountHealth, engagement, avgSeats };
}

function ReportCard({ title, value, subtitle }) {
  return (
    <div className="card p-4" style={{ borderColor: 'var(--color-border)' }}>
      <p className="text-xs font-medium uppercase" style={{ color: 'var(--color-text-secondary)' }}>{title}</p>
      <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--color-text-primary)' }}>{value}</p>
      <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>{subtitle}</p>
    </div>
  );
}
